import { useState, useEffect, useMemo } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  TextInput,
  Checkbox,
  Paper,
  Divider,
  SimpleGrid,
  Button,
  Group,
  Stack,
  Text,
  Table,
  Badge,
  Collapse,
  Loader,
  ActionIcon,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconChevronDown, IconChevronRight, IconSearch, IconX } from '@tabler/icons-react'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { useCanOrder } from './shared/useCanOrder'
import { SIGNATURE_REQUIRED_TOOLTIP } from './shared/constants'
import { TooltipButton } from './shared/TooltipButton'
import {
  loadCustomPanels,
  createCustomPanel,
  updateCustomPanel,
  deleteCustomPanel,
  PANEL_VENDOR_ACCUREFERENCE,
} from './customPanelStorage'
import type { CustomTestPanel } from './customPanelStorage'
import { CustomPanelModal } from './CustomPanelModal'
import { CustomPanelsSection } from './CustomPanelsSection'
import {
  ACCU_REFERENCE_QUESTIONNAIRE_ID,
  ACCU_PANEL_COMPONENTS,
  createInitialAccuReferenceFormState,
} from './accuReferenceFormData'
import type { AccuReferenceFormState } from './accuReferenceFormData'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import type { CartItem } from './shared/OrderCart'
import { OrderReviewSummary } from './shared/OrderReviewSummary'
import type { ReviewSection } from './shared/OrderReviewSummary'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { getErrorMessage, logError } from './shared/errorUtils'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapAccuReferenceToPdf } from './pdf/mapAccuReferenceToPdf'
import type { AutoPopulateData } from './pdf/pdfTypes'
import { useTestCatalog } from './shared/useTestCatalog'
import type { CatalogTest } from './shared/useTestCatalog'

type Props = {
  patient: Patient
  onBack: () => void
}

export const AccuReferenceRequisitionForm = ({ patient, onBack }: Props) => {
  const {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
  } = useAutoPopulate(patient)
  const signature = useSignature()
  const medplum = useMedplum()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  // Test catalog — fetch ALL AccuReference tests
  const { tests: allTests, loading: testsLoading } = useTestCatalog('accureference', {
    _count: 500,
  })

  // Group tests by category
  const testsByCategory = useMemo(() => {
    const map = new Map<string, CatalogTest[]>()
    for (const test of allTests) {
      const cat = test.category || 'other'
      if (!map.has(cat)) map.set(cat, [])
      map.get(cat)!.push(test)
    }
    return map
  }, [allTests])

  // Test lookup map for cart display and PDF
  const testMap = useMemo(() => {
    const map = new Map<string, CatalogTest>()
    for (const t of allTests) map.set(t.code, t)
    return map
  }, [allTests])

  // Group 'general' category tests into alphabetical ranges for individual tests section
  const individualGroups = useMemo(() => {
    const generalTests = testsByCategory.get('general') || []
    const groups: { category: string; tests: CatalogTest[] }[] = [
      { category: 'A – E', tests: [] },
      { category: 'F – L', tests: [] },
      { category: 'M – R', tests: [] },
      { category: 'S – Z', tests: [] },
    ]
    for (const test of generalTests) {
      const first = test.name.charAt(0).toUpperCase()
      if (first <= 'E') groups[0].tests.push(test)
      else if (first <= 'L') groups[1].tests.push(test)
      else if (first <= 'R') groups[2].tests.push(test)
      else groups[3].tests.push(test)
    }
    return groups.filter((g) => g.tests.length > 0)
  }, [testsByCategory])

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const form = useForm<AccuReferenceFormState>({
    initialValues: createInitialAccuReferenceFormState(),
    validateInputOnBlur: true,
  })

  useEffect(() => {
    if (providerDefaults.organizationName) {
      form.setValues({
        organizationName: providerDefaults.organizationName,
        orgPhone: providerDefaults.orgPhone,
        orgFax: providerDefaults.orgFax,
        orgAddress: providerDefaults.orgAddress,
        orgCity: providerDefaults.orgCity,
        orgState: providerDefaults.orgState,
        orgZip: providerDefaults.orgZip,
      })
    }
  }, [providerDefaults.organizationName]) // eslint-disable-line react-hooks/exhaustive-deps

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId: ACCU_REFERENCE_QUESTIONNAIRE_ID,
    generatePdfBytes: () =>
      generateBeHumanOrderPdf(mapAccuReferenceToPdf(form.values, autoPopulateData, testMap)),
    buildFileName: () =>
      `AccuReference_Order_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
    getFormData: () => form.values as unknown as Record<string, unknown>,
    vendorName: 'AccuReference',
    laboratoryOrganizationName: 'AccuReference',
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
  })

  // Auto-apply saved signature
  useEffect(() => {
    if (signature.savedSignature && !form.values.signatureDataUrl) {
      form.setFieldValue('signatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'signatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  // Custom panels
  const [customPanels, setCustomPanels] = useState<CustomTestPanel[]>([])
  const [customPanelsLoading, setCustomPanelsLoading] = useState(true)
  const [panelModalOpen, setPanelModalOpen] = useState(false)
  const [editingPanel, setEditingPanel] = useState<CustomTestPanel | null>(null)
  const [panelSaving, setPanelSaving] = useState(false)

  useEffect(() => {
    const profile = medplum.getProfile() as import('@medplum/fhirtypes').Practitioner | undefined
    if (!profile?.id) return
    setCustomPanelsLoading(true)
    medplum
      .readResource('Practitioner', profile.id)
      .then((fresh) =>
        setCustomPanels(loadCustomPanels(medplum, fresh, PANEL_VENDOR_ACCUREFERENCE)),
      )
      .catch((err) => logError('Custom panel load', err))
      .finally(() => setCustomPanelsLoading(false))
  }, [medplum])

  const handleToggleCustomPanel = (testCodes: string[]) => {
    const current = form.values.selectedTests
    const allSelected = testCodes.every((code) => current.includes(code))
    if (allSelected) {
      form.setFieldValue(
        'selectedTests',
        current.filter((code) => !testCodes.includes(code)),
      )
    } else {
      const newCodes = testCodes.filter((code) => !current.includes(code))
      form.setFieldValue('selectedTests', [...current, ...newCodes])
    }
  }

  const handleCreatePanel = async (name: string, testCodes: string[]) => {
    const profile = medplum.getProfile() as import('@medplum/fhirtypes').Practitioner | undefined
    if (!profile?.id) return
    setPanelSaving(true)
    try {
      const updated = await createCustomPanel(
        medplum,
        profile.id,
        name,
        testCodes,
        PANEL_VENDOR_ACCUREFERENCE,
      )
      setCustomPanels(updated)
      setPanelModalOpen(false)
      setEditingPanel(null)
      notifications.show({
        title: 'Panel Created',
        message: `"${name}" has been saved.`,
        color: 'green',
      })
    } catch (err) {
      logError('Custom panel create', err)
      notifications.show({
        title: 'Failed to create panel',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setPanelSaving(false)
    }
  }

  const handleUpdatePanel = async (panelId: string, name: string, testCodes: string[]) => {
    const profile = medplum.getProfile() as import('@medplum/fhirtypes').Practitioner | undefined
    if (!profile?.id) return
    setPanelSaving(true)
    try {
      const updated = await updateCustomPanel(
        medplum,
        profile.id,
        panelId,
        { name, testCodes },
        PANEL_VENDOR_ACCUREFERENCE,
      )
      setCustomPanels(updated)
      setPanelModalOpen(false)
      setEditingPanel(null)
      notifications.show({
        title: 'Panel Updated',
        message: `"${name}" has been saved.`,
        color: 'green',
      })
    } catch (err) {
      logError('Custom panel update', err)
      notifications.show({
        title: 'Failed to update panel',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setPanelSaving(false)
    }
  }

  /**
   * Deleting a panel is destructive, so it asks the way the rest of the app asks —
   * a styled modal naming the panel, not the browser's own confirm() (PD-1586).
   */
  const handleDeletePanel = (panelId: string) => {
    const panel = customPanels.find((candidate) => candidate.id === panelId)
    modals.openConfirmModal({
      title: panel ? `Delete “${panel.name}”?` : 'Delete this panel?',
      children: (
        <Text size="sm">
          This removes the panel from My Test Panels. Tests you have already selected stay on the
          form.
        </Text>
      ),
      labels: { confirm: 'Delete panel', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => void deletePanel(panelId),
    })
  }

  const deletePanel = async (panelId: string) => {
    const profile = medplum.getProfile() as import('@medplum/fhirtypes').Practitioner | undefined
    if (!profile?.id) return
    try {
      const updated = await deleteCustomPanel(
        medplum,
        profile.id,
        panelId,
        PANEL_VENDOR_ACCUREFERENCE,
      )
      setCustomPanels(updated)
      notifications.show({
        title: 'Panel Deleted',
        message: 'The panel has been removed.',
        color: 'blue',
      })
    } catch (err) {
      logError('Custom panel delete', err)
      notifications.show({
        title: 'Failed to delete panel',
        message: getErrorMessage(err),
        color: 'red',
      })
    }
  }

  // UI state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [testSearch, setTestSearch] = useState('')
  const [showReview, setShowReview] = useState(false)

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  // Toggle test code in unified selectedTests array
  const toggleTest = (code: string) => {
    const current = form.values.selectedTests
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code]
    form.setFieldValue('selectedTests', next)
  }

  // Select-all helpers for specific categories
  const makeSelectAll = (category: string) => {
    const tests = testsByCategory.get(category) || []
    const codes = tests.map((t) => t.code)
    const allSelected =
      codes.length > 0 && codes.every((c) => form.values.selectedTests.includes(c))
    const someSelected = !allSelected && codes.some((c) => form.values.selectedTests.includes(c))
    const toggle = (checked: boolean) => {
      const current = form.values.selectedTests
      if (checked) {
        const newCodes = codes.filter((c) => !current.includes(c))
        form.setFieldValue('selectedTests', [...current, ...newCodes])
      } else {
        form.setFieldValue(
          'selectedTests',
          current.filter((c) => !codes.includes(c)),
        )
      }
    }
    return { allSelected, someSelected, toggle }
  }

  const cancer = makeSelectAll('cancer-markers')
  const micro = makeSelectAll('microbiology')
  const gyn = makeSelectAll('gynecology')

  // Filter individual tests by search
  const filteredIndividualGroups = useMemo(() => {
    if (!testSearch.trim()) return individualGroups
    const q = testSearch.toLowerCase()
    return individualGroups
      .map((group) => ({
        ...group,
        tests: group.tests.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            t.code.toLowerCase().includes(q) ||
            (t.specimen || '').toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.tests.length > 0)
  }, [testSearch, individualGroups])

  // Auto-expand categories matching search
  useEffect(() => {
    if (!testSearch.trim()) return
    setExpandedSections((prev) => {
      const next = new Set(prev)
      for (const group of filteredIndividualGroups) next.add(group.category)
      return next
    })
  }, [testSearch, filteredIndividualGroups])

  const totalSelectedCount = form.values.selectedTests.length

  // Cart items with panel component subItems
  const cartItems: CartItem[] = useMemo(() => {
    const items: CartItem[] = []
    for (const code of form.values.selectedTests) {
      const test = testMap.get(code)
      const components = ACCU_PANEL_COMPONENTS[code]
      items.push({
        id: `${test?.categoryDisplay || 'Test'}::${code}`,
        code,
        name: test?.name || code,
        category: test?.categoryDisplay || 'Test',
        ...(components ? { subItems: components } : {}),
      })
    }
    return items
  }, [form.values.selectedTests, testMap])

  const handleCartRemove = (id: string) => {
    const [, ...rest] = id.split('::')
    const code = rest.join('::')
    form.setFieldValue(
      'selectedTests',
      form.values.selectedTests.filter((c) => c !== code),
    )
  }

  const reviewSections: ReviewSection[] = useMemo(() => {
    const sections: ReviewSection[] = []
    const specimen = [
      { label: 'Collection Date', value: form.values.specimenCollectionDate },
      {
        label: 'Collection Time',
        value: form.values.specimenCollectionTime
          ? `${form.values.specimenCollectionTime} ${form.values.specimenCollectionAmPm}`
          : '',
      },
      { label: 'STAT', value: form.values.isStat ? 'Yes' : '' },
      { label: 'Fasting', value: form.values.isFasting ? 'Yes' : '' },
    ]
    if (specimen.some((i) => i.value)) {
      sections.push({ title: 'Specimen Collection', items: specimen })
    }
    if (
      form.values.selectedTests.some((c) => testMap.get(c)?.category === 'microbiology') &&
      form.values.microbiologySource
    ) {
      sections.push({
        title: 'Microbiology',
        items: [{ label: 'Source', value: form.values.microbiologySource }],
      })
    }
    if (form.values.selectedTests.some((c) => testMap.get(c)?.category === 'gynecology')) {
      const gynItems = [
        { label: 'Biopsy Sites', value: form.values.gynBiopsySites.join(', ') },
        { label: 'LMP', value: form.values.gynLmp },
        { label: 'Source', value: form.values.gynSource },
      ]
      if (gynItems.some((i) => i.value)) {
        sections.push({ title: 'Gynecology', items: gynItems })
      }
    }
    return sections
  }, [form.values, testMap])

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('signatureDataUrl', dataUrl)
    form.setFieldValue('signatureDate', date)
    signature.closeSignatureModal()
  }

  const handleReviewOrder = () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before reviewing.',
        color: 'red',
      })
      return
    }
    if (!form.values.signatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before reviewing.',
        color: 'red',
      })
      return
    }
    if (totalSelectedCount === 0) {
      notifications.show({
        title: 'No Tests Selected',
        message: 'Please select at least one test before reviewing.',
        color: 'red',
      })
      return
    }
    setShowReview(true)
  }

  const handleSubmitOrder = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before submitting.',
        color: 'red',
      })
      return
    }
    if (!form.values.signatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before submitting.',
        color: 'red',
      })
      return
    }
    if (totalSelectedCount === 0) {
      notifications.show({
        title: 'No Tests Selected',
        message: 'Please select at least one test before submitting.',
        color: 'red',
      })
      return
    }
    await submission.handleSubmitOrder()
  }

  // Reusable test table with specimen badges
  const renderTestTable = (tests: CatalogTest[]) => {
    const hasSpecimen = tests.some((t) => t.specimen)
    return (
      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40} />
            <Table.Th w={80}>Code</Table.Th>
            <Table.Th>Test Name</Table.Th>
            {hasSpecimen && <Table.Th w={90}>Specimen</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tests.map((test) => (
            <Table.Tr key={test.code}>
              <Table.Td>
                <Checkbox
                  checked={form.values.selectedTests.includes(test.code)}
                  onChange={() => toggleTest(test.code)}
                />
              </Table.Td>
              <Table.Td>
                <Text size="xs" className="font-mono">
                  {test.code}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs" fw={500}>
                  {test.name}
                </Text>
              </Table.Td>
              {hasSpecimen && (
                <Table.Td>
                  <Badge size="xs" variant="light" color="gray">
                    {test.specimen ?? '—'}
                  </Badge>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  const renderSectionToggle = (label: string, count: number) => (
    <Button
      variant="subtle"
      size="sm"
      fullWidth
      justify="space-between"
      rightSection={
        expandedSections.has(label) ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />
      }
      onClick={() => toggleSection(label)}
      className="!text-left"
    >
      <Group gap="xs">
        <Text fw={600} size="sm">
          {label}
        </Text>
        <Badge size="xs" variant="light">
          {count} tests
        </Badge>
      </Group>
    </Button>
  )

  const panelTests = testsByCategory.get('organ-disease-panels') || []
  const molecularTests = testsByCategory.get('molecular') || []
  const cancerTests = testsByCategory.get('cancer-markers') || []
  const microTests = testsByCategory.get('microbiology') || []
  const gynTests = testsByCategory.get('gynecology') || []

  return (
    <OrderFormLayout
      cart={<OrderCart items={cartItems} onRemove={handleCartRemove} vendorLabel="AccuReference" />}
      header={
        <FormHeader
          title="AccuReference General Test Requisition"
          subtitle="Select tests and complete the order"
          onBack={onBack}
          onPreview={submission.handlePreviewPdf}
          onSubmit={handleSubmitOrder}
          exporting={submission.exporting}
          submitting={submission.submitting}
          orderSubmitted={submission.orderSubmitted}
          hideActions
          labError={submission.labError}
          orgError={orgError}
          warnings={insuranceWarning ? [insuranceWarning] : []}
        />
      }
    >
      {/* ===== REVIEW ===== */}
      {showReview ? (
        <Stack gap="lg">
          <OrderReviewSummary
            vendorName="AccuReference"
            tests={cartItems}
            diagnosisText={form.values.diagnosisText}
            sections={reviewSections}
            signatureDataUrl={form.values.signatureDataUrl}
            signatureDate={form.values.signatureDate}
            onEdit={() => setShowReview(false)}
            onSubmit={handleSubmitOrder}
            onPreview={submission.handlePreviewPdf}
            submitting={submission.submitting}
            exporting={submission.exporting}
            orderSubmitted={submission.orderSubmitted}
            isPractitioner={isPractitioner}
            canOrderLoading={canOrderLoading}
            labError={submission.labError}
            orgError={orgError}
          />
        </Stack>
      ) : (
        <Stack gap="lg">
          {testsLoading && (
            <Group justify="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading test catalog...
              </Text>
            </Group>
          )}

          {!testsLoading && (
            <>
              {/* ===== ICD-10 / DIAGNOSES ===== */}
              <DiagnosisCodesSection
                value={form.values.diagnosisText}
                defaultCodes={diagnosisDefaults}
                onChange={(v) => form.setFieldValue('diagnosisText', v)}
              />

              {/* ===== MY TEST PANELS ===== */}
              <Paper p="md" withBorder>
                <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                  MY TEST PANELS
                </Text>
                <Text size="xs" c="dimmed" mb="sm">
                  Your saved custom test groups. Selecting a panel auto-checks its individual tests.
                </Text>
                <CustomPanelsSection
                  panels={customPanels}
                  selectedTests={form.values.selectedTests}
                  testLookup={testMap}
                  onTogglePanel={handleToggleCustomPanel}
                  onCreatePanel={() => {
                    setEditingPanel(null)
                    setPanelModalOpen(true)
                  }}
                  onEditPanel={(panel) => {
                    setEditingPanel(panel)
                    setPanelModalOpen(true)
                  }}
                  onDeletePanel={handleDeletePanel}
                  loading={customPanelsLoading}
                />
              </Paper>

              <CustomPanelModal
                opened={panelModalOpen}
                onClose={() => {
                  setPanelModalOpen(false)
                  setEditingPanel(null)
                }}
                onSave={
                  editingPanel
                    ? (name, codes) => handleUpdatePanel(editingPanel.id, name, codes)
                    : handleCreatePanel
                }
                existingPanel={editingPanel}
                saving={panelSaving}
                testGroups={individualGroups}
              />

              {/* ===== TEST PANELS ===== */}
              {panelTests.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    TEST PANELS
                  </Text>
                  <Text size="xs" c="dimmed" mb="sm">
                    Each panel component can be ordered individually. See page 2 of the form for
                    panel details.
                  </Text>
                  {renderSectionToggle('Test Panels', panelTests.length)}
                  <Collapse in={expandedSections.has('Test Panels')}>
                    <div className="mr-4 ml-4">{renderTestTable(panelTests)}</div>
                  </Collapse>
                </Paper>
              )}

              {/* ===== MOLECULAR DIAGNOSTICS ===== */}
              {molecularTests.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    MOLECULAR DIAGNOSTICS
                  </Text>
                  {renderSectionToggle('Molecular Diagnostics', molecularTests.length)}
                  <Collapse in={expandedSections.has('Molecular Diagnostics')}>
                    <div className="mr-4 ml-4">{renderTestTable(molecularTests)}</div>
                  </Collapse>
                </Paper>
              )}

              {/* ===== CANCER MARKERS ===== */}
              {cancerTests.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    CANCER MARKERS
                  </Text>
                  {renderSectionToggle('Cancer Markers', cancerTests.length)}
                  <Collapse in={expandedSections.has('Cancer Markers')}>
                    <div className="mr-4 ml-4">
                      <Checkbox
                        label="Select All"
                        checked={cancer.allSelected}
                        indeterminate={cancer.someSelected}
                        onChange={(e) => cancer.toggle(e.target.checked)}
                        mb="sm"
                      />
                      {renderTestTable(cancerTests)}
                    </div>
                  </Collapse>
                </Paper>
              )}

              {/* ===== MICROBIOLOGY ===== */}
              {microTests.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    MICROBIOLOGY
                  </Text>
                  {renderSectionToggle('Microbiology', microTests.length)}
                  <Collapse in={expandedSections.has('Microbiology')}>
                    <div className="mr-4 ml-4">
                      <Checkbox
                        label="Select All"
                        checked={micro.allSelected}
                        indeterminate={micro.someSelected}
                        onChange={(e) => micro.toggle(e.target.checked)}
                        mb="sm"
                      />
                      {renderTestTable(microTests)}
                      <TextInput
                        label="Source"
                        placeholder="Specify specimen source..."
                        value={form.values.microbiologySource}
                        onChange={(e) => form.setFieldValue('microbiologySource', e.target.value)}
                        mt="sm"
                        w={300}
                      />
                    </div>
                  </Collapse>
                </Paper>
              )}

              {/* ===== GYNECOLOGY ===== */}
              {gynTests.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    GYNECOLOGY
                  </Text>
                  {renderSectionToggle('Gynecology', gynTests.length)}
                  <Collapse in={expandedSections.has('Gynecology')}>
                    <div className="mr-4 ml-4">
                      <Checkbox
                        label="Select All"
                        checked={gyn.allSelected}
                        indeterminate={gyn.someSelected}
                        onChange={(e) => gyn.toggle(e.target.checked)}
                        mb="sm"
                      />
                      {renderTestTable(gynTests)}
                      <Divider my="sm" />
                      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                        <Checkbox.Group
                          label="Biopsy Site"
                          value={form.values.gynBiopsySites}
                          onChange={(v) => form.setFieldValue('gynBiopsySites', v)}
                        >
                          <Group mt={4}>
                            <Checkbox value="CX" label="CX" />
                            <Checkbox value="VG" label="VG" />
                          </Group>
                        </Checkbox.Group>
                        <TextInput
                          label="LMP"
                          placeholder="Last Menstrual Period"
                          value={form.values.gynLmp}
                          onChange={(e) => form.setFieldValue('gynLmp', e.target.value)}
                        />
                        <TextInput
                          label="Source"
                          placeholder="Specimen source..."
                          value={form.values.gynSource}
                          onChange={(e) => form.setFieldValue('gynSource', e.target.value)}
                        />
                      </SimpleGrid>
                    </div>
                  </Collapse>
                </Paper>
              )}

              {/* ===== INDIVIDUAL TESTS ===== */}
              {individualGroups.length > 0 && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    INDIVIDUAL TESTS
                  </Text>
                  <TextInput
                    placeholder="Search by test name, code, or specimen..."
                    leftSection={<IconSearch size={16} />}
                    value={testSearch}
                    onChange={(e) => setTestSearch(e.target.value)}
                    mb="sm"
                    rightSection={
                      testSearch ? (
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          aria-label="Clear search"
                          onClick={() => setTestSearch('')}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      ) : null
                    }
                    rightSectionPointerEvents={testSearch ? 'all' : 'none'}
                  />
                  <Stack gap="xs">
                    {filteredIndividualGroups.map((group) => (
                      <div key={group.category}>
                        {renderSectionToggle(group.category, group.tests.length)}
                        <Collapse in={expandedSections.has(group.category)}>
                          <div className="mr-4 ml-4">{renderTestTable(group.tests)}</div>
                        </Collapse>
                      </div>
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* ===== SIGNATURE ===== */}
              <SignatureSection
                signatureDataUrl={form.values.signatureDataUrl}
                signatureDate={form.values.signatureDate}
                savedSignature={signature.savedSignature}
                signatureModalOpen={signature.signatureModalOpen}
                onOpenModal={signature.openSignatureModal}
                onCloseModal={signature.closeSignatureModal}
                onConfirm={handleSignatureConfirm}
                onSaveSignature={signature.handleSaveSignature}
                certificationText="Please order only if medically necessary and appropriate for individual patients."
              />

              {/* ===== ACTIONS ===== */}
              <Group justify="flex-end" pb="xl">
                <Checkbox
                  label="Dispatch Phlebotomist"
                  checked={form.values.dispatchPhlebotomist}
                  onChange={(e) => form.setFieldValue('dispatchPhlebotomist', e.target.checked)}
                  disabled
                />
                <Button variant="default" onClick={onBack}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={submission.handlePreviewPdf}
                  loading={submission.exporting}
                >
                  Download PDF
                </Button>
                <TooltipButton
                  color="teal"
                  disabled={submission.orderSubmitted || !form.values.signatureDataUrl}
                  disabledReason={!form.values.signatureDataUrl ? SIGNATURE_REQUIRED_TOOLTIP : null}
                  onClick={handleReviewOrder}
                >
                  {submission.orderSubmitted ? 'Order Submitted' : 'Review Order'}
                </TooltipButton>
              </Group>
            </>
          )}
        </Stack>
      )}
    </OrderFormLayout>
  )
}
